/**
 * Lab manualGroupBetting multi ui-select — pick several players from searchHierarchyNew.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Field, Input, Spinner } from '@w7/ui'
import { searchHierarchyNew } from '@/api/map/member'

const DEBOUNCE_MS = 100
const SUGGEST_LIMIT = 20

export type GroupMemberPickerProps = {
  value: string[]
  onChange: (usernames: string[]) => void
  disabled?: boolean
}

export function GroupMemberPicker({ value, onChange, disabled }: GroupMemberPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const membersQ = useQuery({
    queryKey: ['cm', 'security', 'group-member-search', debounced],
    queryFn: async () => {
      const rows = await searchHierarchyNew({ filter: debounced.trim(), page: 1 })
      return rows.map((r) => r.username).filter(Boolean)
    },
    enabled: open && !disabled,
    staleTime: 10_000,
    retry: false,
  })

  const selected = new Set(value.map((u) => u.toLowerCase()))
  const suggestions = (membersQ.data ?? [])
    .filter((name) => !selected.has(name.toLowerCase()))
    .slice(0, SUGGEST_LIMIT)

  const pick = (name: string) => {
    if (selected.has(name.toLowerCase())) return
    onChange([...value, name])
    setDraft('')
    setOpen(false)
  }

  const remove = (name: string) => {
    onChange(value.filter((u) => u !== name))
  }

  return (
    <div className="cm-group-member-picker" ref={rootRef}>
      <Field label="Select users" required>
        {({ id, describedBy }) => (
          <div className="cm-cheater-player-wrap">
            {value.length ? (
              <ul className="cm-group-member-chips" aria-label="Selected users">
                {value.map((name) => (
                  <li key={name}>
                    <button
                      type="button"
                      className="cm-group-member-chip"
                      disabled={disabled}
                      onClick={() => remove(name)}
                      aria-label={`Remove ${name}`}
                    >
                      {name} ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <Input
              id={id}
              aria-describedby={describedBy}
              placeholder="Enter or select member name to search"
              value={draft}
              disabled={disabled}
              aria-expanded={open}
              aria-controls="cm-group-member-suggest"
              aria-autocomplete="list"
              aria-busy={membersQ.isFetching}
              onChange={(e) => {
                setDraft(e.target.value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
            />
            {open && !disabled ? (
              <ul
                id="cm-group-member-suggest"
                className="cm-cheater-player-suggest"
                role="listbox"
              >
                {membersQ.isFetching && suggestions.length === 0 ? (
                  <li className="cm-cheater-player-empty" role="presentation">
                    <Spinner size={14} /> Searching…
                  </li>
                ) : null}
                {suggestions.length === 0 && !membersQ.isFetching ? (
                  <li className="cm-cheater-player-empty">No players found</li>
                ) : (
                  suggestions.map((name) => (
                    <li key={name}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="cm-cheater-player-option"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          pick(name)
                        }}
                      >
                        {name}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}
      </Field>
    </div>
  )
}
